import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  Chip,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import KeyIcon from '@mui/icons-material/Key';
import RefreshIcon from '@mui/icons-material/Refresh';
import Parse from 'parse';
import RegenerateKeyConfirmDialog from './RegenerateKeyConfirmDialog';

const CreateEditOperatorDialog = ({ open, onClose, onSave, operator, mode }) => {
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    phone: '',
    password: '',
    domains: [],
    operatorLogo: null
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [domainInput, setDomainInput] = useState('');
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [provisionKey, setProvisionKey] = useState(null);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);
  const [keyExists, setKeyExists] = useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);

  // Display-currency mappings (presentation-only aliases for internal SC/GC)
  const DEFAULT_CURRENCY_MAPPINGS = [
    { internalCurrency: 'SC', displayCode: 'SC', isEnabled: true },
    { internalCurrency: 'GC', displayCode: 'GC', isEnabled: true }
  ];
  const [currencyOptions, setCurrencyOptions] = useState({ SC: [], GC: [] });
  const [currencyMappings, setCurrencyMappings] = useState(DEFAULT_CURRENCY_MAPPINGS);

  // Load currency options + the operator's chosen mappings when the dialog opens
  useEffect(() => {
    if (!open) return;
    const loadCurrencies = async () => {
      try {
        if (mode === 'edit' && operator?.id) {
          const res = await Parse.Cloud.run('getOperatorCurrencies', { operatorId: operator.id });
          if (res.success) {
            setCurrencyOptions(res.options || { SC: [], GC: [] });
            setCurrencyMappings(res.mappings?.length ? res.mappings : DEFAULT_CURRENCY_MAPPINGS);
          }
        } else {
          const res = await Parse.Cloud.run('listCurrencies');
          if (res.success) {
            const opts = { SC: [], GC: [] };
            res.currencies
              .filter((c) => c.isActive !== false)
              .forEach((c) => {
                if (opts[c.internalCurrency]) opts[c.internalCurrency].push(c);
              });
            setCurrencyOptions(opts);
            setCurrencyMappings(DEFAULT_CURRENCY_MAPPINGS);
          }
        }
      } catch (err) {
        console.error('Failed to load currencies:', err);
      }
    };
    loadCurrencies();
  }, [open, mode, operator]);

  const updateMapping = (internalCurrency, patch) => {
    setCurrencyMappings((prev) =>
      prev.map((m) => (m.internalCurrency === internalCurrency ? { ...m, ...patch } : m))
    );
  };

  // Ensure the currently-selected code is always a selectable option
  const optionsForRow = (m) => {
    const list = currencyOptions[m.internalCurrency] || [];
    if (m.displayCode && !list.some((o) => o.code === m.displayCode)) {
      return [...list, { id: `_cur_${m.displayCode}`, code: m.displayCode, name: '' }];
    }
    return list;
  };

  // Check if provision key exists when dialog opens in edit mode
  useEffect(() => {
    if (mode === 'edit' && operator && open) {
      const operatorKey = operator.operatorKey;
      const iv = operator.iv;
      const exists = !!(operatorKey && iv);
      setKeyExists(exists);
    } else {
      setKeyExists(false);
    }
  }, [mode, operator, open]);

  // Populate form when editing
  useEffect(() => {
    if (mode === 'edit' && operator) {
      setFormData({
        name: operator.name || '',
        username: operator.username || '',
        email: operator.email || '',
        phone: operator.phone || '',
        password: '',
        domains: operator.domains || [],
        operatorLogo: null
      });
      setLogoPreview(operator.operatorLogo || null);
    } else {
      setFormData({
        name: '',
        username: '',
        email: '',
        phone: '',
        password: '',
        domains: [],
        operatorLogo: null
      });
      setLogoPreview(null);
    }
    setDomainInput('');
    setErrors({});
    setProvisionKey(null);
    setKeyCopied(false);
  }, [mode, operator, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };
  // Name: no digits allowed
  const handleNameChange = (e) => {
    const cleanedValue = e.target.value.replace(/[^A-Za-z ]/g, '');

    setFormData((prev) => ({
      ...prev,
      name: cleanedValue
    }));
  };
  const handleUsernameChange = (e) => {
    const cleanedValue = e.target.value.replace(/[^A-Za-z ]/g, '');

    setFormData((prev) => ({
      ...prev,
      username: cleanedValue
    }));
  };

  // Phone: digits only, max 10
  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 10) value = value.slice(0, 10);
    setFormData({ ...formData, phone: value });
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setErrors({ ...errors, operatorLogo: 'Only image files are allowed (JPEG, PNG, GIF, WebP)' });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrors({ ...errors, operatorLogo: 'File size must be less than 5MB' });
      return;
    }

    // Validate image dimensions (must be 512x512)
    const img = new Image();
    img.onload = () => {
      if (img.width !== 512 || img.height !== 512) {
        setErrors({
          ...errors,
          operatorLogo: `Logo must be exactly 512 × 512 pixels. Uploaded image is ${img.width} × ${img.height} pixels.`
        });
        // Reset the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const base64Data = reader.result.split(',')[1]; // Remove data:image/xxx;base64, prefix
        setFormData({
          ...formData,
          operatorLogo: {
            imageData: base64Data,
            fileName: file.name,
            fileType: file.type
          }
        });
        setLogoPreview(reader.result);
        setErrors({ ...errors, operatorLogo: '' });
      };
      reader.readAsDataURL(file);
    };
    img.onerror = () => {
      setErrors({ ...errors, operatorLogo: 'Failed to load image. Please try a different file.' });
    };
    img.src = URL.createObjectURL(file);
  };

  const handleRemoveLogo = () => {
    setFormData({ ...formData, operatorLogo: null });
    setLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getDomainName = (domain) => {
    return typeof domain === 'string' ? domain : domain.domainName;
  };

  const handleAddDomain = () => {
    const newDomain = domainInput.trim();
    if (!newDomain) return;

    // Check if domain already exists
    const exists = formData.domains.some((d) => getDomainName(d) === newDomain);
    if (!exists) {
      // In create mode, add as string. In edit mode, add as object without id (new domain)
      const domainToAdd = mode === 'create' ? newDomain : { domainName: newDomain };
      setFormData({ ...formData, domains: [...formData.domains, domainToAdd] });
      setDomainInput('');
      if (errors.domains) {
        setErrors({ ...errors, domains: '' });
      }
    }
  };

  const handleRemoveDomain = (domainToRemove) => {
    const domainNameToRemove = getDomainName(domainToRemove);
    setFormData({
      ...formData,
      domains: formData.domains.filter((d) => getDomainName(d) !== domainNameToRemove)
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    // Password required only for create mode
    if (mode === 'create') {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }
    }

    // Domain Callback URL required only for create mode (at least one)
    if (mode === 'create') {
      if (!formData.domains || formData.domains.length === 0) {
        newErrors.domains = 'At least one Domain Callback URL is required';
      }
    }

    // Operator Logo required only for create mode
    if (mode === 'create') {
      if (!formData.operatorLogo && !logoPreview) {
        newErrors.operatorLogo = 'Operator Logo is required (512 × 512 pixels)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      let operatorId;
      if (mode === 'create') {
        const createData = {
          name: formData.name,
          username: formData.username,
          email: formData.email,
          password: formData.password,
          domains: formData.domains
        };
        if (formData.phone) {
          createData.phone = formData.phone;
        }
        if (formData.operatorLogo) {
          createData.operatorLogo = formData.operatorLogo;
        }
        const createResult = await Parse.Cloud.run('createOperator', createData);
        operatorId = createResult?.operator?.id;
      } else {
        const updateData = {
          operatorId: operator.id,
          name: formData.name,
          username: formData.username,
          email: formData.email,
          domains: formData.domains
        };
        if (formData.phone) {
          updateData.phone = formData.phone;
        }
        if (formData.operatorLogo) {
          updateData.operatorLogo = formData.operatorLogo;
        }
        await Parse.Cloud.run('updateOperator', updateData);
        operatorId = operator.id;
      }

      // Save display-currency mappings (non-fatal — operator is already saved)
      if (operatorId) {
        try {
          await Parse.Cloud.run('setOperatorCurrencies', {
            operatorId,
            mappings: currencyMappings
          });
        } catch (err) {
          console.error('Failed to save currency mappings:', err);
        }
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving operator:', error);
      setErrors({ submit: error.message || 'Failed to save operator' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const handleGenerateProvisionKey = async (forceRegenerate = false) => {
    if (!operator?.id) return;

    // Coerce to a primitive: an onClick handler can hand us a React synthetic
    // event, and Parse.Cloud.run deep-encodes its params. Encoding an event
    // walks into the DOM/fiber graph and blows the stack.
    const force = forceRegenerate === true;

    setGeneratingKey(true);
    try {
      const result = await Parse.Cloud.run('provisionAgentSecureKey', {
        agentId: operator.id,
        forceRegenerate: force
      });
      setProvisionKey(result.secureKey);
      setKeyCopied(false);
      setKeyExists(true);
      setShowRegenerateConfirm(false);
    } catch (error) {
      console.error('Error generating provision key:', error);
      setErrors({ ...errors, provisionKey: error.message || 'Failed to generate provision key' });
    } finally {
      setGeneratingKey(false);
    }
  };

  const handleRegenerateClick = () => {
    setShowRegenerateConfirm(true);
  };

  const handleConfirmRegenerate = () => {
    handleGenerateProvisionKey(true);
  };

  const handleCancelRegenerate = () => {
    setShowRegenerateConfirm(false);
  };

  const handleCopyKey = () => {
    if (provisionKey) {
      navigator.clipboard.writeText(provisionKey);
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 2000);
    }
  };

  const handleDownloadKey = () => {
    if (!provisionKey) return;

    const blob = new Blob([provisionKey], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `operator-${operator?.username || 'key'}-provision-key.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#2B2B2B',
      color: '#fff',
      '& input': { padding: '10px 14px' }
    },
    '& .MuiFormHelperText-root': { color: '#F44336' }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { backgroundColor: '#242424', m: 0 } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            backgroundColor: '#242424',
            color: '#FFC107',
            pb: 2
          }}
        >
          <Typography sx={{ fontSize: '20px', fontWeight: 600 }}>
            {mode === 'create' ? 'Create New Operator' : 'Edit Operator'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#242424', pt: 2, pb: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {errors.submit && (
              <Typography sx={{ color: '#F44336', fontSize: '14px', textAlign: 'center' }}>
                {errors.submit}
              </Typography>
            )}

            {/* Row 1: Name and Username */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', mb: 0.5 }}>
                  Name *
                </Typography>
                <TextField
                  name="name"
                  fullWidth
                  placeholder="Enter name"
                  value={formData.name}
                  onChange={handleNameChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  disabled={loading}
                  sx={textFieldSx}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', mb: 0.5 }}>
                  Username *
                </Typography>
                <TextField
                  name="username"
                  fullWidth
                  placeholder="Enter username"
                  value={formData.username}
                  onChange={handleUsernameChange}
                  error={!!errors.username}
                  helperText={errors.username}
                  disabled={loading}
                  sx={textFieldSx}
                />
              </Box>
            </Box>

            {/* Row 2: Email and Phone */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', mb: 0.5 }}>
                  Email *
                </Typography>
                <TextField
                  name="email"
                  type="email"
                  fullWidth
                  placeholder="Enter email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  disabled={loading}
                  sx={textFieldSx}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', mb: 0.5 }}>
                  Phone
                </Typography>
                <TextField
                  name="phone"
                  fullWidth
                  placeholder="Enter phone"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  disabled={loading}
                  sx={textFieldSx}
                  inputProps={{ maxLength: 10 }}
                />
              </Box>
            </Box>

            {/* Password (only for create mode) */}
            {mode === 'create' && (
              <Box>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', mb: 0.5 }}>
                  Password *
                </Typography>
                <TextField
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  fullWidth
                  placeholder="Enter password"
                  value={formData.password}
                  onChange={handleChange}
                  error={!!errors.password}
                  helperText={errors.password}
                  disabled={loading}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={textFieldSx}
                />
              </Box>
            )}

            {/* Domains */}
            <Box>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', mb: 0.5 }}>
                Domain Callback URL {mode === 'create' ? '*' : ''}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  fullWidth
                  placeholder="Enter domain (e.g., example.com)"
                  value={domainInput}
                  onChange={(e) => setDomainInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddDomain())}
                  disabled={loading}
                  sx={textFieldSx}
                />
                <Button
                  variant="contained"
                  onClick={handleAddDomain}
                  disabled={loading || !domainInput.trim()}
                  sx={{
                    backgroundColor: '#FFC107',
                    color: '#000',
                    minWidth: '50px',
                    '&:hover': { backgroundColor: '#FFD54F' },
                    '&.Mui-disabled': {
                      backgroundColor: 'rgba(255, 255, 255, 0.12)',
                      color: 'rgba(255, 255, 255, 0.5)'
                    }
                  }}
                >
                  <AddIcon />
                </Button>
              </Box>
              {formData.domains.length > 0 && formData.domains.some((d) => getDomainName(d)) && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {formData.domains
                    .filter((domain) => getDomainName(domain))
                    .map((domain, index) => (
                      <Chip
                        key={getDomainName(domain) + index}
                        label={getDomainName(domain)}
                        onDelete={() => handleRemoveDomain(domain)}
                        deleteIcon={<CloseIcon sx={{ color: '#fff !important' }} />}
                        sx={{
                          backgroundColor: 'rgba(255, 193, 7, 0.2)',
                          color: '#FFC107',
                          '& .MuiChip-deleteIcon': { color: '#FFC107' }
                        }}
                      />
                    ))}
                </Box>
              )}
              {errors.domains && (
                <Typography sx={{ color: '#F44336', fontSize: '12px', mt: 0.5 }}>
                  {errors.domains}
                </Typography>
              )}
            </Box>

            {/* Display Currencies */}
            <Box>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', mb: 0.5 }}>
                Display Currencies
              </Typography>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '12px', mb: 1.5 }}>
                Choose the label shown to this operator&apos;s players. The backend keeps processing
                the internal currency (SC / GC) unchanged.
              </Typography>
              {currencyMappings.map((m) => (
                <Box
                  key={m.internalCurrency}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 1.5,
                    flexWrap: 'wrap'
                  }}
                >
                  <Box sx={{ minWidth: 36, color: '#fff', fontWeight: 600 }}>
                    {m.internalCurrency}
                  </Box>
                  <Box sx={{ color: 'rgba(255, 255, 255, 0.4)' }}>→</Box>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Display as</InputLabel>
                    <Select
                      value={m.displayCode}
                      label="Display as"
                      onChange={(e) =>
                        updateMapping(m.internalCurrency, { displayCode: e.target.value })
                      }
                      disabled={loading}
                      sx={{
                        backgroundColor: '#2B2B2B',
                        color: '#fff',
                        '& .MuiSvgIcon-root': { color: '#fff' },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255,255,255,0.3)'
                        }
                      }}
                    >
                      {optionsForRow(m).map((opt) => (
                        <MenuItem key={opt.id} value={opt.code}>
                          {opt.code}
                          {opt.name ? ` — ${opt.name}` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={m.isEnabled !== false}
                        onChange={(e) =>
                          updateMapping(m.internalCurrency, { isEnabled: e.target.checked })
                        }
                        disabled={loading}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFC107' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#FFC107'
                          }
                        }}
                      />
                    }
                    label="Enabled"
                    sx={{ color: 'rgba(255, 255, 255, 0.7)', ml: 0.5 }}
                  />
                </Box>
              ))}
            </Box>

            {/* Provision Key Section (only in edit mode) */}
            {mode === 'edit' && (
              <Box>
                {keyExists && !provisionKey ? (
                  <>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        mb: 1
                      }}
                    >
                      <Box component="span" sx={{ color: '#fff', fontSize: '14px' }}>
                        Provision Key:
                      </Box>{' '}
                      <Box component="span" sx={{ color: '#fff' }}>
                        Provision key already generated for this operator. You can regenerate the
                        key by clicking the regenerate button.
                      </Box>
                    </Typography>
                    <Button
                      onClick={handleRegenerateClick}
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      sx={{
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                        color: '#fff',
                        textTransform: 'none',
                        fontSize: '13px',
                        '&:hover': {
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                          backgroundColor: 'rgba(255, 255, 255, 0.05)'
                        }
                      }}
                    >
                      Regenerate
                    </Button>
                  </>
                ) : !provisionKey ? (
                  <>
                    <Button
                      onClick={() => handleGenerateProvisionKey(false)}
                      variant="contained"
                      disabled={generatingKey}
                      startIcon={generatingKey ? <CircularProgress size={16} /> : <KeyIcon />}
                      sx={{
                        backgroundColor: '#FFC107',
                        color: '#000',
                        textTransform: 'none',
                        fontSize: '13px',
                        fontWeight: 600,
                        '&:hover': { backgroundColor: '#FFD54F' },
                        '&.Mui-disabled': {
                          backgroundColor: 'rgba(255, 255, 255, 0.12)',
                          color: 'rgba(255, 255, 255, 0.5)'
                        }
                      }}
                    >
                      {generatingKey ? 'Generating...' : 'Generate Provision Key'}
                    </Button>
                    <Typography
                      sx={{
                        mt: 1,
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontSize: '12px',
                        fontStyle: 'italic'
                      }}
                    >
                      Note: This key will be generated only once. Please copy or download it and
                      keep it safe.
                    </Typography>
                  </>
                ) : (
                  <>
                    <TextField
                      fullWidth
                      value={provisionKey}
                      InputProps={{
                        readOnly: true,
                        sx: {
                          fontFamily: 'monospace',
                          fontSize: '13px',
                          color: '#4CAF50'
                        }
                      }}
                      sx={{
                        mb: 1,
                        ...textFieldSx
                      }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <Button
                        onClick={handleCopyKey}
                        variant="outlined"
                        startIcon={<ContentCopyIcon />}
                        sx={{
                          flex: 1,
                          borderColor: keyCopied ? '#4CAF50' : 'rgba(255, 255, 255, 0.3)',
                          color: keyCopied ? '#4CAF50' : '#fff',
                          textTransform: 'none',
                          fontSize: '13px',
                          '&:hover': {
                            borderColor: keyCopied ? '#4CAF50' : 'rgba(255, 255, 255, 0.5)',
                            backgroundColor: 'rgba(255, 255, 255, 0.05)'
                          }
                        }}
                      >
                        {keyCopied ? 'Copied!' : 'Copy Key'}
                      </Button>
                      <Button
                        onClick={handleDownloadKey}
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        sx={{
                          flex: 1,
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                          color: '#fff',
                          textTransform: 'none',
                          fontSize: '13px',
                          '&:hover': {
                            borderColor: 'rgba(255, 255, 255, 0.5)',
                            backgroundColor: 'rgba(255, 255, 255, 0.05)'
                          }
                        }}
                      >
                        Download Key
                      </Button>
                    </Box>
                    <Typography
                      sx={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontSize: '12px',
                        fontStyle: 'italic'
                      }}
                    >
                      Important: Save this key now. You won&apos;t be able to see it again after
                      closing this dialog.
                    </Typography>
                  </>
                )}

                {errors.provisionKey && (
                  <Typography sx={{ color: '#F44336', fontSize: '12px', mt: 1 }}>
                    {errors.provisionKey}
                  </Typography>
                )}
              </Box>
            )}

            {/* Operator Logo */}
            <Box>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', mb: 0.5 }}>
                Operator Logo {mode === 'create' ? '*' : ''}
              </Typography>
              {logoPreview ? (
                <Box sx={{ position: 'relative', width: 'fit-content' }}>
                  <Avatar
                    src={logoPreview}
                    sx={{ width: 80, height: 80, borderRadius: '8px' }}
                    variant="rounded"
                  />
                  <IconButton
                    onClick={handleRemoveLogo}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      backgroundColor: '#F44336',
                      color: '#fff',
                      '&:hover': { backgroundColor: '#D32F2F' }
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Box
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    width: '100%',
                    height: 100,
                    borderRadius: '8px',
                    border: '2px dashed rgba(255, 255, 255, 0.3)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: '#FFC107',
                      backgroundColor: 'rgba(255, 193, 7, 0.05)'
                    }
                  }}
                >
                  <CloudUploadIcon sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: 32 }} />
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px', mt: 0.5 }}>
                    Click to upload logo (512 × 512 px)
                  </Typography>
                </Box>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                onChange={handleLogoUpload}
                style={{ display: 'none' }}
              />
              {errors.operatorLogo && (
                <Typography sx={{ color: '#F44336', fontSize: '12px', mt: 0.5 }}>
                  {errors.operatorLogo}
                </Typography>
              )}
            </Box>

            {/* Buttons Row */}
            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
              <Button
                onClick={handleClose}
                variant="outlined"
                fullWidth
                disabled={loading}
                sx={{
                  flex: 1,
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  color: '#fff',
                  borderRadius: '4px',
                  py: 1,
                  fontSize: '14px',
                  textTransform: 'capitalize',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{
                  flex: 1,
                  backgroundColor: '#FFC107',
                  color: '#000',
                  borderRadius: '4px',
                  py: 1,
                  fontSize: '14px',
                  fontWeight: 600,
                  textTransform: 'capitalize',
                  '&:hover': { backgroundColor: '#FFD54F' }
                }}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: '#000' }} />
                ) : mode === 'create' ? (
                  'Create'
                ) : (
                  'Save'
                )}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Regenerate Confirmation Dialog */}
      <RegenerateKeyConfirmDialog
        open={showRegenerateConfirm}
        onClose={handleCancelRegenerate}
        onConfirm={handleConfirmRegenerate}
        loading={generatingKey}
      />
    </>
  );
};

export default CreateEditOperatorDialog;
